import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, X, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface MessageToastData {
  id: string;
  messageId: number;
  senderName: string;
  subject: string;
  messagePreview?: string;
  createdAt: string;
}

const TOAST_DURATION_MS = 7000;

const MessageToast: React.FC = () => {
  const { user, userDomain } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<MessageToastData[]>([]);

  const messagesPath = userDomain === 'admin'
    ? '/admin/messages'
    : userDomain === 'company'
      ? '/company/messages'
      : userDomain === 'audit'
        ? '/audit/messages'
        : '/compliance/messages';

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    if (!user) return;

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as Omit<MessageToastData, 'id'>;
      const toastId = `msg-${detail.messageId}-${Date.now()}`;
      const toast: MessageToastData = { ...detail, id: toastId };

      setToasts(prev => [...prev, toast]);

      // Auto-dismiss after TOAST_DURATION_MS
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toastId));
      }, TOAST_DURATION_MS);
    };

    window.addEventListener('newMessageReceived', handler);
    return () => window.removeEventListener('newMessageReceived', handler);
  }, [user]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[99999] flex flex-col gap-3 pointer-events-none">
      {toasts.map(toast => (
        <div
          key={toast.id}
          className="pointer-events-auto w-80 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-slide-in-right"
          style={{ animation: 'slideInRight 0.3s ease-out' }}
        >
          {/* Progress bar */}
          <div
            className="h-1 bg-blue-500"
            style={{ animation: `shrink ${TOAST_DURATION_MS}ms linear forwards` }}
          />

          <div className="p-4">
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className="flex-shrink-0 w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                <MessageSquare size={16} className="text-blue-600" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">New Message</p>
                  <button
                    onClick={() => dismiss(toast.id)}
                    className="p-0.5 text-slate-300 hover:text-slate-500 rounded transition-colors flex-shrink-0"
                  >
                    <X size={12} />
                  </button>
                </div>
                <p className="text-sm font-semibold text-slate-900 truncate">{toast.subject}</p>
                <p className="text-xs text-slate-500 mt-0.5">From {toast.senderName}</p>
                {toast.messagePreview && (
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{toast.messagePreview}</p>
                )}
              </div>
            </div>

            {/* Action button */}
            <button
              onClick={() => {
                dismiss(toast.id);
                navigate(`${messagesPath}?id=${toast.messageId}`);
              }}
              className="mt-3 w-full flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold py-2 px-3 rounded-lg transition-colors"
            >
              View Message
              <ArrowRight size={12} />
            </button>
          </div>
        </div>
      ))}

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(110%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        @keyframes shrink {
          from { width: 100%; }
          to   { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default MessageToast;
